import { useEffect, useState } from 'react';

import type { DealDismissReason, DealKind, FieldDismissReason } from '../../types';
import styles from './DealsPage.module.css';

// Card-shape spec §10.3 + §10.4 — structured-reason modal applied to
// both deal-level and field-level dismiss flows. "Other" reveals a
// required free-text input; all other reasons make the note optional.
//
// Slice J §J4 — deal-level reasons are deal-kind aware. Candidates
// (new deals) and Affinity opps (field updates) get different reason
// lists.

const NEW_DEAL_REASONS: { key: DealDismissReason; label: string; desc: string }[] = [
  { key: 'portco_operations', label: 'Portfolio company operations', desc: 'Operations of an existing portfolio company — not a new deal.' },
  // WP2/S2.2 (portco-transactions spec §5): the WORDING changed, and the change is the point.
  // Exits are now tracked as transactions with their own stage ladder and board band, so
  // dismissing is no longer what you do with a real exit — it is what you do with exit chatter
  // about a company we do not own. The old copy ('Exit activity for an existing portco')
  // actively told an operator to dismiss the thing the system is now built to track.
  { key: 'portco_exit', label: 'Unwanted exit chatter', desc: 'A sale process we are not running — e.g. a banker pitching a company we do not own. A real exit of OUR portfolio company is a transaction, not a dismissal.' },
  { key: 'duplicate', label: 'Duplicate of an existing deal', desc: 'Same deal already in pipeline.' },
  { key: 'newsletter_missed', label: 'Newsletter that slipped through', desc: 'Should have been filtered upstream.' },
  { key: 'pass_terminal', label: 'Log pass', desc: 'Explicit pass decision; record terminal status.' },
  { key: 'other', label: 'Other', desc: 'Free-text required.' },
];

const FIELD_UPDATE_REASONS: { key: DealDismissReason; label: string; desc: string }[] = [
  { key: 'wrong_match', label: "Incorrect match — this isn't the same deal", desc: 'Resolver bound to the wrong opportunity.' },
  { key: 'not_an_update', label: 'This is a new deal, not an update', desc: 'Should have created a candidate, not a field update.' },
  { key: 'poor_update_quality', label: 'Correct deal but the suggested updates are wrong', desc: 'Right opp; values are off.' },
  { key: 'not_actionable', label: 'Update is correct but not worth logging', desc: 'Low-value change; skip.' },
  { key: 'pass_terminal', label: 'Log pass', desc: 'Explicit pass decision; record terminal status.' },
  { key: 'other', label: 'Other', desc: 'Free-text required.' },
];

/**
 * Exported so the §1.2 fact editor can offer the SAME taxonomy inline.
 *
 * A removal there reveals its reason in place rather than stacking a second dialog, and the rule that
 * there is no free text where a vocabulary exists has to survive the move — a second copy of these five
 * values would drift from this one and split the tuning signal between two vocabularies.
 */
export const FIELD_REASONS: { key: FieldDismissReason; label: string; desc: string }[] = [
  { key: 'wrong_value', label: 'Wrong value', desc: 'Factual error in the proposed value.' },
  { key: 'not_material', label: 'Not material', desc: 'Could be true but the change is not worth pushing.' },
  { key: 'out_of_scope', label: 'Out of scope', desc: 'This field should not be auto-touched on this deal.' },
  { key: 'keep_my_value', label: 'Keep my value', desc: 'My prior value is correct; respect stickiness.' },
  { key: 'other', label: 'Other', desc: 'Free-text required.' },
];

interface Props {
  open: boolean;
  granularity: 'deal' | 'field';
  // Slice J §J4 — deal-kind drives which deal-level reason list shows.
  // Field-granularity ignores this prop (field reasons aren't kind-split).
  dealKind?: DealKind;
  subjectLabel: string; // e.g. "Project Mario" or "advisor_firm = Goldman"
  onClose: () => void;
  onConfirm: (reason: DealDismissReason | FieldDismissReason, note?: string) => void;
  submitting?: boolean;
}

export function DismissReasonModal({
  open,
  granularity,
  dealKind = 'candidate',
  subjectLabel,
  onClose,
  onConfirm,
  submitting = false,
}: Props) {
  const reasons =
    granularity === 'field'
      ? FIELD_REASONS
      : dealKind === 'candidate'
        ? NEW_DEAL_REASONS
        : FIELD_UPDATE_REASONS;
  const [reason, setReason] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Reset when re-opening for a different target.
  useEffect(() => {
    if (open) {
      setReason(null);
      setNote('');
      setError(null);
    }
  }, [open, subjectLabel]);

  if (!open) return null;

  const isOther = reason === 'other';
  const canSubmit =
    !!reason && !submitting && (!isOther || note.trim().length >= 5);

  const handleSubmit = () => {
    if (!reason) return;
    if (isOther && note.trim().length < 5) {
      setError('Note must be at least 5 characters when reason is Other.');
      return;
    }
    if (note.length > 500) {
      setError('Note exceeds 500 characters.');
      return;
    }
    onConfirm(
      reason as DealDismissReason | FieldDismissReason,
      note.trim() || undefined,
    );
  };

  return (
    <div
      className={styles.modalBackdrop}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* role="dialog" is load-bearing beyond a11y: the F.8 record-nav guard turns the arrow keys
          off while any non-record-panel dialog is MOUNTED. Without it this modal took no focus at
          all, so the keys stayed live and a confirm could land on a record swapped in behind it. */}
      <div className={styles.modal} role="dialog" aria-modal="true"
           aria-label={`Dismiss ${granularity === 'deal' ? 'deal' : 'field'}`}>
        <h3 className={styles.modalTitle}>
          Dismiss {granularity === 'deal' ? 'deal' : 'field'}
        </h3>
        <div className={styles.modalSubtitle}>{subjectLabel}</div>

        <div className={styles.reasonList}>
          {reasons.map((r) => (
            <label key={r.key} className={styles.reasonRow}>
              <input
                type="radio"
                name="dismiss-reason"
                value={r.key}
                checked={reason === r.key}
                onChange={() => {
                  setReason(r.key);
                  setError(null);
                }}
              />
              <div>
                <div className={styles.reasonLabel}>{r.label}</div>
                <div className={styles.reasonDesc}>{r.desc}</div>
              </div>
            </label>
          ))}
        </div>

        <label className={styles.noteLabel}>
          Note {isOther ? '(required)' : '(optional)'}
          <textarea
            className={styles.noteInput}
            rows={3}
            maxLength={500}
            placeholder={
              isOther
                ? 'Required when reason is Other — at least 5 characters.'
                : 'Optional context.'
            }
            value={note}
            onChange={(e) => {
              setNote(e.target.value);
              setError(null);
            }}
          />
        </label>

        {error && <div className={styles.modalError}>{error}</div>}

        <div className={styles.modalActions}>
          <button
            type="button"
            className={styles.modalCancel}
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.modalConfirm}
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {submitting ? 'Dismissing…' : 'Dismiss'}
          </button>
        </div>
      </div>
    </div>
  );
}
